// Hands-free finish corrections while the camera auto-adds.
//
// The user says "that was reverse holo" while cards keep flowing. Which card?
// The one that was on screen when they STARTED speaking — not whichever add
// happens to finish last. So every scan gets an immutable token at scan time,
// the utterance is bound to the token active at its first word on the audio
// timeline, and the correction waits for THAT scan's add to resolve, then
// patches THAT entry, once. Nothing here retargets, re-adds or changes
// quantity; the backend validates ownership, single copy, finish, revision and
// replays the same action id without a second write.

const FINISHES = { normal: 'Normal', holofoil: 'Holofoil', reverse: 'Reverse Holofoil' };

// Parse a spoken finish correction. Returns
//   { printing }                      a clear single finish
//   { ambiguous: true, reason }       a correction that must be clarified, no guess
//   null                              not a finish correction at all
export function parseFinishCorrection(text) {
  // ASR variants heard in practice: "reverse hollow foil", "holo foil", "holographic".
  const s = String(text || '').toLowerCase().replace(/[^\p{L}\p{N}\s'-]/gu, ' ').replace(/\s+/g, ' ').trim()
    .replace(/\bhollow\b/g, 'holo').replace(/\bholo foil\b/g, 'holofoil').replace(/\bholla\b/g, 'holo');
  if (!s) return null;
  const question = /^(how|what|which|where|why|when|who|is it|are|does|do|can|could|would|should i)\b/.test(s) || /\bworth\b|\bprice\b|\bvalue\b|\bhow much\b/.test(s);
  if (question) return null;
  const correctionCue = /^(that|this|it|no|actually|oops|wait|make|mark|set|change)\b|\b(that was|this was|it was|that's|it's|this is|that is|should be|make it|mark it|set it|change it|actually)\b/.test(s);
  const unsupported = /\b(first edition|1st edition|promo|1st ed)\b/.test(s);
  // Negations ("not reverse", "no holo", "isn't foil") are removed before
  // looking for a positive finish: a negative alone never implies a finish.
  const nonHolo = /\bnon[- ]?holo(?:foil)?\b/g;                 // the everyday name for Normal
  const s1 = s.replace(nonHolo, ' normal ');
  const NEG = /\b(?:not|no|isn't|isnt|wasn't|wasnt|never)[- ]?(?:a |the )?(?:reverse[- ]?holo(?:foil)?|rev holo|reverse|holo(?:foil|graphic)?|foil|shiny|normal|regular|plain|mirror)\b/g;
  const negatives = (s1.match(NEG) || []).length;
  const pos = s1.replace(NEG, ' ');
  const REVERSE = /\b(reverse[- ]?holo(?:foil)?|rev holo|reverse|mirror)\b/g;
  const reverse = REVERSE.test(pos);
  const holo = /\b(holo|holofoil|holographic)\b/.test(pos.replace(REVERSE, ' '));
  const normal = /\b(normal|regular|plain)\b/.test(pos);
  const vague = /\b(foil|shiny|glossy|sparkly)\b/.test(pos.replace(REVERSE, ' ').replace(/\bholofoil\b/g, ' '));
  const finishes = [reverse && 'reverse', holo && 'holofoil', normal && 'normal'].filter(Boolean);
  if (unsupported) return { ambiguous: true, reason: 'unsupported_finish' };
  if (finishes.length > 1) return { ambiguous: true, reason: 'several_finishes' };
  if (finishes.length === 0) {
    if (negatives) return { ambiguous: true, reason: 'negative_only' };   // "not reverse" ≠ normal
    if (vague) return { ambiguous: true, reason: 'unclear_finish' };      // "foil"/"shiny": holo or reverse?
    return null;
  }
  if (!correctionCue && s.split(' ').length > 4) return null; // a remark that merely mentions "holo"
  // Grammar: a clear SINGULAR correction only. Plural or multi-card phrasing
  // ("those were holo", "both reverse", "all of them") must not mutate the one
  // speech-time copy; a named card is returned so the caller can verify it
  // against the bound copy instead of trusting the deictic.
  if (/\b(those|these|they|them|both|all|every|each|the two|two|three|several|cards|ones|copies)\b/.test(pos)) return { ambiguous: true, reason: 'plural' };
  const STOP = new Set(['hey', 'hi', 'hello', 'yeah', 'yep', 'nope', 'nah', 'well', 'right', 'bindarr', 'that', 'this', 'it', 'one', 'was', 'is', "it's", "that's", 'its', 'card', 'make', 'mark', 'set', 'should', 'be', 'actually', 'change', 'the', 'a', 'an', 'just', 'as', 'to', 'oops', 'wait', 'no', 'yes', 'okay', 'ok', 'please', 'sorry', 'um', 'uh', 'so', 'and', 'not', 'non', 'holo', 'holofoil', 'holographic', 'reverse', 'rev', 'normal', 'regular', 'plain', 'foil', 'shiny', 'mirror', 'finish', 'version', 'printing', 'copy', 'last', 'scan', 'scanned', 'previous', 'now', 'then', 'again', 'i', 'mean', 'meant', 'think', 'oh', 'hmm', 'really', 'like', 'of', 'in', 'on', 'for', 'with', 'my', 'me', 'we', 'you', 'were', 'are', "isn't", "wasn't", 'edition', 'first', '1st', 'promo']);
  const mention = pos.split(' ').map(w => w.replace(/^[-']+|[-']+$/g, '')).filter(w => w.length >= 3 && !STOP.has(w) && /[\p{L}]/u.test(w));
  return mention.length ? { printing: FINISHES[finishes[0]], mention } : { printing: FINISHES[finishes[0]] };
}

// A named card must be the bound copy: every mentioned word has to appear in
// that card's name (or set); otherwise the user meant another card → ask.
export function mentionMatchesTarget(mention, target) {
  if (!mention || !mention.length) return true;
  if (!target) return false;
  const hay = `${target.name || ''} ${target.set_name || ''}`.toLowerCase();
  return mention.every(w => hay.includes(w.toLowerCase()));
}

// Utterance start on the audio timeline → wall clock.
export function speechWallTime(sessionStartedAt, startMs) {
  if (!Number.isFinite(sessionStartedAt) || !Number.isFinite(startMs)) return null;
  return sessionStartedAt + startMs;
}

// One id per (scan, session, utterance). The utterance identity is the local
// speech ONSET (exact, not shifted by ASR revisions); the ASR start is the
// fallback. A retry or late delegation of the same utterance reuses it.
export function correctionActionId(token, utterance) {
  const u = utterance && typeof utterance === 'object' ? utterance : { start_ms: utterance };
  const session = String(u.session || 'nosession').replace(/[^a-zA-Z0-9_.:-]/g, '');
  const ident = Number.isFinite(u.onset) ? `o${Math.round(u.onset)}` : Number.isFinite(u.start_ms) ? `s${Math.round(u.start_ms)}` : 'x';
  return `scanfix:${token}:${session}:${ident}`.slice(0, 160);
}

// The ledger of scans in this camera session and their adds.
export class ScanLedger {
  constructor() { this.scans = []; this.waiters = new Map(); this.claims = new Map(); this.resolution = new Map(); }

  // Resolves when the activation has a recognised card; rejects when the
  // capture failed/ambiguous or after timeoutMs.
  waitForResolution(token, timeoutMs = 20000) {
    const rec = this.scans.find(s => s.token === token);
    if (!rec) return Promise.reject(new Error('unknown scan'));
    if (rec.resolved) return Promise.resolve(rec);
    if (rec.unresolvedReason) return Promise.reject(new Error('capture unresolved'));
    return new Promise((resolve, reject) => {
      const list = this.resolution.get(token) || []; this.resolution.set(token, list);
      const waiter = { resolve: null, reject: null };
      const timer = setTimeout(() => { this.resolution.set(token, (this.resolution.get(token) || []).filter(w => w !== waiter)); reject(new Error('recognition pending')); }, timeoutMs);
      waiter.resolve = (v) => { clearTimeout(timer); resolve(v); }; waiter.reject = (e) => { clearTimeout(timer); reject(e); };
      list.push(waiter);
    });
  }

  // Activate an immutable token at the CAPTURE signal, before recognition:
  // speech right after the shutter binds to this activation, not to the
  // previous copy. `noteScan` later enriches the same activation with the
  // recognised card; a failed or ambiguous capture leaves it unresolved, and
  // an unresolved activation is a barrier (clarify), never a fall-through.
  activate({ token, at = Date.now() }) {
    if (!token) return null;
    const existing = this.scans.find(s => s.token === token);
    if (existing) return existing;
    const rec = { token, at, card_id: null, name: '', set_name: '', number: null, printing: null, quantity: 1, resolved: false, alternatives: [], scope: undefined };
    this.scans.push(rec); this.scans.sort((a, b) => a.at - b.at);
    return rec;
  }

  // The validated-shape context the backend expects (`context.scanner`):
  // the copy bound at speech time, recent committed scans, pending captures,
  // current scope and mode with their versions. Ids only; the backend
  // re-verifies every one of them against its own rows and commits.
  contextSnapshot({ bound = null, scope, mode, game = null, lang = null, limit = 5 } = {}) {
    const rec = (r) => r ? ({ token: r.token, entry_id: this.add(r.token)?.id ?? null, card_id: r.card_id, name: r.name, number: r.number, set_id: r.set_id ?? null, printing: this.add(r.token)?.printing ?? r.printing ?? null, alternatives: r.alternatives || [], ...(r.recount ? { kind: 'recount', token: r.recount.observation_id, session_id: r.recount.session_id, printing: r.recount.printing || r.printing || null } : {}) }) : null;
    const committed = this.scans.filter(r => this.add(r.token)?.status === 'added' || r.recount).slice(-limit).reverse();
    return {
      active: true, mode: mode?.mode ?? null, mode_version: mode?.version ?? null, game, lang,
      scope: { set_ids: scope?.set_ids || [], label: scope?.label || null }, scope_version: scope?.version ?? null,
      bound: rec(bound), recent: [...new Map(committed.map(r => { const value = rec(r); return [value.token, value]; })).values()],
      pending_tokens: this.scans.filter(r => !this.add(r.token) || this.add(r.token).status === 'pending').map(r => r.token).slice(-10)
    };
  }

  // Called when the card is recognised (and again, idempotently, before the
  // add request). Enriches the activation; a token seen for the first time
  // here is activated at `at`.
  noteScan({ token, card, at = Date.now(), printing = null, quantity = 1, alternatives = null, scope = undefined }) {
    if (!token) return null;
    let rec = this.scans.find(s => s.token === token);
    if (!rec) { rec = this.activate({ token, at }); }
    if (scope !== undefined && rec.scope === undefined) rec.scope = scope;   // snapshot at capture, never rewritten
    if (Array.isArray(alternatives)) rec.alternatives = alternatives.map(a => ({ card_id: a.card_id ?? a.id, name: a.name || '', number: a.number ?? null, set_id: a.set_id ?? null, set_name: a.set_name || '' })).filter(a => a.card_id);
    if (card) { rec.card_id = card.id ?? card.card_id ?? rec.card_id; rec.name = card.name || rec.name; rec.set_name = card.set_name || rec.set_name; rec.number = card.number ?? rec.number; rec.resolved = true; rec.unresolvedReason = null; }
    if (printing) rec.printing = printing; if (quantity) rec.quantity = quantity;
    if (!this.waiters.has(token) && rec.resolved) this.waiters.set(token, { status: 'pending', ids: null, id: null, printing, quantity, resolvers: [] });
    if (rec.resolved) for (const w of (this.resolution.get(token) || []).splice(0)) w.resolve(rec);
    return rec;
  }

  // Organize mode: the activation resolved to a card the user already OWNS.
  // The binding is the explicit owned copy (single) or the card with its
  // copies to choose from; it is never an add and never a scan commit.
  noteOwned({ token, card, copies = [], alternatives = null }) {
    const rec = this.scans.find(s => s.token === token) || this.activate({ token });
    if (Array.isArray(alternatives)) rec.alternatives = alternatives.filter(a => a && a.card_id).map(a => ({ card_id: a.card_id, name: a.name || '', number: a.number ?? null, set_id: a.set_id ?? null, set_name: a.set_name || '' }));
    rec.card_id = card?.id ?? card?.card_id ?? rec.card_id; rec.name = card?.name || rec.name; rec.number = card?.number ?? rec.number; rec.set_name = card?.set_name || rec.set_name;
    rec.resolved = true; rec.unresolvedReason = null;
    rec.owned = { entry_id: copies.length === 1 ? copies[0].entry_id : null, copies: copies.map(c => ({ entry_id: c.entry_id, printing: c.printing, location_name: c.location_name, page: c.page })) };
    for (const w of (this.resolution.get(token) || []).splice(0)) w.resolve(rec);
    return rec;
  }

  // Wait until a capture is a recount observation (session known), bounded.
  waitForRecount(token, timeoutMs = 4000) {
    return new Promise((resolve) => { const t0 = Date.now(); const tick = () => { const rec = this.scans.find(r => r.token === token); if (rec?.recount) return resolve(rec); if (Date.now() - t0 >= timeoutMs) return resolve(rec || null); setTimeout(tick, 60); }; tick(); });
  }

  // Recount observations, newest first (chips when no onset bound a count).
  recentRecount(limit = 6) { const seen = new Set(); return [...this.scans].reverse().filter(r => r.recount && !seen.has(r.recount.observation_id) && seen.add(r.recount.observation_id)).slice(0, limit); }

  // Organize RECOUNT: the activation is a persisted observation of an explicit
  // count session. The binding the backend verifies is the observation id (the
  // capture token), the session and the finish the observation was filed under.
  noteRecount({ token, session_id, observation_id = token, card = null, printing = null, alternatives = null }) {
    const rec = this.scans.find(s => s.token === token) || this.activate({ token });
    if (card) { rec.card_id = card.id ?? card.card_id ?? rec.card_id; rec.name = card.name || rec.name; rec.number = card.number ?? rec.number; rec.set_name = card.set_name || rec.set_name; rec.resolved = true; rec.unresolvedReason = null; }
    if (Array.isArray(alternatives)) rec.alternatives = alternatives.filter(a => a && (a.card_id || a.id)).map(a => ({ card_id: a.card_id ?? a.id, name: a.name || '', number: a.number ?? null, set_id: a.set_id ?? null, set_name: a.set_name || '' }));
    rec.recount = { session_id, observation_id, printing: printing || rec.printing || null };
    if (printing) rec.printing = printing;
    for (const w of (this.resolution.get(token) || []).splice(0)) w.resolve(rec);
    return rec;
  }

  // Recognition failed or was ambiguous: the activation stays as a barrier.
  noteUnresolved({ token, reason = 'unresolved' }) {
    const rec = this.scans.find(s => s.token === token); if (!rec) return null;
    rec.resolved = false; rec.unresolvedReason = reason;
    for (const w of (this.resolution.get(token) || []).splice(0)) w.reject(new Error('capture unresolved'));
    return rec;
  }

  // Announce each committed scan token at most once: a replayed successful
  // response for the same token (retry, duplicate event) must not be spoken
  // again. A different token for the same card is a new scan and is announced.
  shouldAnnounce(token) {
    if (!token) return false;
    if (!this.announced) this.announced = new Set();
    if (this.announced.has(token)) return false;
    this.announced.add(token); return true;
  }

  noteAdded({ token, ids, id, printing, quantity }) {
    const w = this.waiters.get(token); if (!w) return;
    w.status = 'added'; w.ids = Array.isArray(ids) ? ids.map(Number) : (id != null ? [Number(id)] : []); w.id = id != null ? Number(id) : (w.ids[0] ?? null);
    if (printing) w.printing = printing; if (quantity) w.quantity = quantity;
    for (const r of w.resolvers.splice(0)) r.resolve(w);
  }

  noteFailed({ token, error }) {
    const w = this.waiters.get(token); if (!w) return;
    w.status = 'failed'; w.error = error || 'add failed';
    for (const r of w.resolvers.splice(0)) r.reject(new Error(w.error));
  }

  scan(token) { return this.scans.find(s => s.token === token) || null; }
  add(token) { return this.waiters.get(token) || null; }

  // The scan active when the user started speaking: the latest scan at or
  // before that moment. Never "the latest scan now".
  targetAt(wallMs) {
    if (!Number.isFinite(wallMs)) return null;
    let best = null;
    for (const s of this.scans) if (s.at <= wallMs) best = s;
    return best;
  }

  // The audio clock is only known within an interval [lower, upper] on the
  // wall clock (see VoiceAdapter.audioToWall). If a scan boundary falls inside
  // the interval the target is ambiguous: return both candidates so the user
  // is asked, never guessed. A late-arriving ASR event does not change this:
  // the mapping uses the segment's start_ms, not its arrival time.
  targetIn(interval) {
    if (!interval || !Number.isFinite(interval.lower) || !Number.isFinite(interval.upper)) return { target: null, ambiguous: false, candidates: [] };
    const lo = Math.min(interval.lower, interval.upper), hi = Math.max(interval.lower, interval.upper);
    const a = this.targetAt(lo), b = this.targetAt(hi);
    if (a === b) return { target: a, ambiguous: false, candidates: a ? [a] : [] };
    const candidates = this.scans.filter(s => s === a || (s.at > lo && s.at <= hi));
    return { target: null, ambiguous: true, candidates };
  }

  // Bind an utterance to a scan using BOTH clocks conservatively:
  //   - local microphone speech onsets (exact wall clock, measured locally):
  //     each onset in the utterance's window snapshots the scan active then;
  //   - the ASR timeline mapped through the measured origin interval, as a
  //     cross-check only (the official docs say it is not a wall clock).
  // Every candidate must agree on one scan; otherwise ask, never guess.
  bindUtterance({ onsets = [], interval = null } = {}) {
    // The FIRST measured local onset of the utterance is authoritative: the
    // user said "that" about the card on screen when they STARTED speaking.
    // Later onsets (a pause and resume) and the ASR timeline mapped through
    // the origin interval never override it or make it ambiguous; a scan that
    // lands mid-utterance is irrelevant. No onset → no automatic target (the
    // caller may ask which card, never guess). The ASR mapping is reported as
    // a diagnostic only.
    if (!onsets.length) return { target: null, ambiguous: false, candidates: [], basis: 'none', reason: 'no_onset' };
    const first = Math.min(...onsets.filter(Number.isFinite));
    const target = this.targetAt(first);
    const asr = interval ? this.targetIn(interval) : null;
    const diag = { first_onset: first, later_onsets: onsets.filter(at => at !== first), asr_target: asr?.target?.token ?? null, asr_ambiguous: !!asr?.ambiguous };
    if (!target) return { target: null, ambiguous: false, candidates: [], basis: 'onset', reason: 'no_scan_before_onset', diag };
    return { target, ambiguous: false, candidates: [target], basis: 'onset', diag };
  }

  // Candidate chips from backend-returned tokens (only tokens this ledger knows).
  candidatesFromTokens(tokens) {
    return (Array.isArray(tokens) ? tokens : []).map(tk => this.scan(typeof tk === 'string' ? tk : tk?.token)).filter(Boolean)
      .map(sc => ({ token: sc.token, card_id: sc.card_id, name: sc.name, number: sc.number, entry_id: this.add(sc.token)?.id ?? null, scan: sc }));
  }

  // Recent committed scans (newest first), for asking "which card?" when no
  // onset was measured. A question with candidates, never a guessed target.
  recentCommitted(limit = 4) {
    return this.scans.filter(r => this.add(r.token)?.status === 'added').slice(-limit).reverse();
  }

  // Resolves with the add record once that exact scan's add has completed.
  waitForAdd(token, timeoutMs = 20000) {
    const w = this.waiters.get(token);
    if (!w) return Promise.reject(new Error(this.scans.some(s => s.token === token && !s.resolved) ? 'capture unresolved' : 'unknown scan'));
    if (w.status === 'added') return Promise.resolve(w);
    if (w.status === 'failed') return Promise.reject(new Error(w.error || 'add failed'));
    return new Promise((resolve, reject) => {
      const waiter = { resolve: null, reject: null };
      const timer = setTimeout(() => { w.resolvers = w.resolvers.filter(r => r !== waiter); reject(new Error('add still pending')); }, timeoutMs);
      waiter.resolve = (value) => { clearTimeout(timer); resolve(value); };
      waiter.reject = (err) => { clearTimeout(timer); reject(err); };
      w.resolvers.push(waiter);
    });
  }

  // Once-only: the first claim of an action id owns it; concurrent or late
  // delegations share that promise. A result that could NOT have written
  // (pending add, clarification, target refused) is released after it
  // settles, so a retry of the SAME binding and id runs again; a result where
  // a write may have happened stays pinned (a retry replays the same id).
  claim(actionId, run) {
    if (this.claims.has(actionId)) return { first: false, promise: this.claims.get(actionId) };
    const promise = Promise.resolve().then(run).then((result) => {
      if (!result || !result.mutationAttempted) this.claims.delete(actionId);
      return result;
    }, (err) => { this.claims.delete(actionId); throw err; });
    this.claims.set(actionId, promise);
    return { first: true, promise };
  }
}

// Run one correction end to end. `api` is injectable:
//   api.target(scan_commit_id, entry_id) → { entry, revision }
//   api.finish({ action_id, scan_commit_id, entry_id, expected_revision, printing }) → { receipt, entry, revision }
// Returns { status: 'saved'|'unchanged'|'clarify'|'refused', ... }; never throws.
export async function runScanCorrection({ ledger, target, printing, utterance, speechStartMs, actionId, api, waitMs = 20000, pendingRetryMs = 400, pendingRetries = 25 }) {
  if (!target) return { status: 'clarify', reason: 'no_target' };
  const action_id = actionId || correctionActionId(target.token, utterance || { start_ms: speechStartMs });
  const { promise } = ledger.claim(action_id, async () => {
    // Bound to a capture still being recognised: wait for the SAME token to
    // resolve (never pick another card); a failed capture is a barrier.
    if (target.resolved === false && !ledger.add(target.token)) {
      try { await ledger.waitForResolution(target.token, waitMs); }
      catch (e) { return /pending/.test(e.message) ? { status: 'refused', reason: 'recognition_pending', target, retryable: true } : { status: 'clarify', reason: 'capture_unresolved', target }; }
    }
    let add;
    try { add = await ledger.waitForAdd(target.token, waitMs); }
    catch (e) { if (/unresolved/.test(e.message)) return { status: 'clarify', reason: 'capture_unresolved', target }; return { status: 'refused', reason: /pending/.test(e.message) ? 'add_pending' : 'add_failed', target, retryable: /pending/.test(e.message) }; }
    if (!add.ids || add.ids.length !== 1) return { status: 'clarify', reason: 'not_single_copy', target };
    const entry_id = add.ids[0];
    let t = null;
    for (let i = 0; i <= pendingRetries; i++) {
      try { t = await api.target(target.token, entry_id); break; }
      catch (e) {
        if (e?.code === 'SCAN_PENDING' && i < pendingRetries) { await new Promise(r => setTimeout(r, pendingRetryMs)); continue; }
        return { status: 'refused', reason: e?.code || 'target_failed', message: e?.message || null, target, entry_id, retryable: e?.code === 'SCAN_PENDING' };
      }
    }
    if (!t || !t.entry) return { status: 'refused', reason: 'target_failed', target, entry_id };
    if (t.entry.printing === printing) return { status: 'unchanged', target, entry_id, printing, action_id };
    try {
      const r = await api.finish({ action_id, scan_commit_id: target.token, entry_id, expected_revision: t.revision, printing });
      // "Saved" only with proof: a receipt for THIS copy and the entry now showing THIS finish.
      const receiptOk = r && r.receipt && Number(r.receipt.entry_id) === Number(entry_id);
      const entryOk = r && r.entry && Number(r.entry.id) === Number(entry_id) && r.entry.printing === printing;
      if (!receiptOk || !entryOk) return { status: 'refused', reason: 'unverified_response', target, entry_id, printing, action_id, mutationAttempted: true, receipt: r?.receipt || null };
      return { status: 'saved', target, entry_id, printing, receipt: r.receipt, entry: r.entry, action_id, mutationAttempted: true };
    } catch (e) {
      return { status: 'refused', reason: e?.code || 'finish_failed', message: e?.message || null, target, entry_id, printing, action_id, mutationAttempted: true, retryable: true };
    }
  });
  return promise;
}

// Spoken feedback after the fact. Names the card so the user can tell which
// copy changed; asks a concise question when nothing should be written.
export function correctionSpeech(result, t = (k) => k) {
  const name = result?.target?.name || '';
  switch (result?.status) {
    case 'saved': return t('scan.voice.saved', { name, finish: result.printing });
    case 'unchanged': return t('scan.voice.unchanged', { name, finish: result.printing });
    case 'clarify':
      if (result.reason === 'no_target') return t('scan.voice.noTarget');
      if (result.reason === 'capture_unresolved') return t('scan.voice.unresolved');
      if (result.reason === 'plural') return t('scan.voice.oneAtATime');
      if (result.reason === 'named_mismatch') return t('scan.voice.namedMismatch', { name, said: (result.mention || []).join(' ') });
      if (result.reason === 'boundary') return t('scan.voice.whichOf', { a: result.candidates?.[0]?.name || '', b: result.candidates?.[result.candidates.length - 1]?.name || '' });
      if (result.reason === 'not_single_copy') return t('scan.voice.notSingle', { name });
      if (result.reason === 'unsupported_finish') return t('scan.voice.unsupported', { name });
      if (result.reason === 'negative_only' || result.reason === 'unclear_finish' || result.reason === 'several_finishes') return name ? t('scan.voice.whichFinish', { name }) : t('scan.voice.whichFinishNoName');
      return t('scan.voice.which', { name });
    default:
      if (result?.reason === 'add_pending' || result?.reason === 'recognition_pending') return t('scan.voice.stillSaving', { name });
      if (result?.reason === 'no_onset') return t('scan.voice.noOnset');
      if (result?.reason === 'add_failed') return t('scan.voice.addFailed', { name });
      if (result?.reason === 'unverified_response' || result?.reason === 'finish_failed') return t('scan.voice.unverified', { name });
      return t('scan.voice.refused', { name });
  }
}


// Exact-copy identity correction ("not that Pokémon"): same binding, wait and
// once-only rules as the finish correction; the backend validates the card
// against the scan's recorded candidates.
export async function runScanIdentity({ ledger, target, card_id, utterance, actionId, api, waitMs = 20000, pendingRetryMs = 400, pendingRetries = 25 }) {
  if (!target) return { status: 'clarify', reason: 'no_target' };
  if (!card_id) return { status: 'clarify', reason: 'choose', target };
  const action_id = actionId || correctionActionId(target.token, utterance || {}).replace(/^scanfix:/, 'scanid:');
  const { promise } = ledger.claim(action_id, async () => {
    if (target.resolved === false && !ledger.add(target.token)) {
      try { await ledger.waitForResolution(target.token, waitMs); }
      catch (e) { return /pending/.test(e.message) ? { status: 'refused', reason: 'recognition_pending', target, retryable: true } : { status: 'clarify', reason: 'capture_unresolved', target }; }
    }
    let add;
    try { add = await ledger.waitForAdd(target.token, waitMs); }
    catch (e) { return { status: 'refused', reason: /pending/.test(e.message) ? 'add_pending' : 'add_failed', target, retryable: /pending/.test(e.message) }; }
    if (!add.ids || add.ids.length !== 1) return { status: 'clarify', reason: 'not_single_copy', target };
    const entry_id = add.ids[0];
    let t = null;
    for (let i = 0; i <= pendingRetries; i++) {
      try { t = await api.target(target.token, entry_id); break; }
      catch (e) { if (e?.code === 'SCAN_PENDING' && i < pendingRetries) { await new Promise(r => setTimeout(r, pendingRetryMs)); continue; } return { status: 'refused', reason: e?.code || 'target_failed', message: e?.message || null, target, entry_id, retryable: e?.code === 'SCAN_PENDING' }; }
    }
    if (!t || !t.entry) return { status: 'refused', reason: 'target_failed', target, entry_id };
    if (t.entry.card_id === card_id) return { status: 'unchanged', target, entry_id, card_id, action_id };
    try {
      const r = await api.identity({ action_id, scan_commit_id: target.token, entry_id, expected_revision: t.revision, card_id });
      const ok = r && r.receipt && Number(r.receipt.entry_id) === Number(entry_id) && r.entry && Number(r.entry.id) === Number(entry_id) && r.entry.card_id === card_id;
      if (!ok) return { status: 'refused', reason: 'unverified_response', target, entry_id, card_id, action_id, mutationAttempted: true, receipt: r?.receipt || null };
      return { status: 'saved', kind: 'identity', target, entry_id, card_id, card: r.card || null, receipt: r.receipt, entry: r.entry, action_id, mutationAttempted: true };
    } catch (e) {
      return { status: 'refused', reason: e?.code || 'identity_failed', message: e?.message || null, target, entry_id, card_id, action_id, mutationAttempted: true, retryable: true };
    }
  });
  return promise;
}

// Versioned scope + mode. A result computed against an older version never
// overwrites a newer choice; scope changes apply to FUTURE captures only
// (each capture snapshots the scope it was taken under).
export class ScanSession {
  constructor() { this.scope = { set_ids: [], label: null, version: 0, changed_at: null }; this.mode = { mode: null, version: 0 }; }
  setScope(sets, { for_version } = {}) {
    if (Number.isInteger(for_version) && for_version < this.scope.version) return { applied: false, reason: 'stale', scope: this.scope };
    const list = Array.isArray(sets) ? sets.filter(s => s && s.id) : [];
    this.scope = { set_ids: list.map(s => String(s.id)), label: list.length ? list.map(s => s.name || s.id).join(', ') : null, version: this.scope.version + 1, changed_at: Date.now() };
    return { applied: true, scope: this.scope };
  }
  addScope(sets, opts) { const cur = this.scope.set_ids; return this.setScope([...cur.map(id => ({ id, name: id })), ...(sets || [])].filter((s, i, a) => a.findIndex(x => String(x.id) === String(s.id)) === i), opts); }
  clearScope(opts) { return this.setScope([], opts); }
  setMode(mode, { for_version } = {}) {
    if (Number.isInteger(for_version) && for_version < this.mode.version) return { applied: false, reason: 'stale', mode: this.mode };
    if (!['add', 'organize'].includes(mode)) return { applied: false, reason: 'invalid', mode: this.mode };
    this.mode = { mode, version: this.mode.version + 1 };
    return { applied: true, mode: this.mode };
  }
}

// Spoken announcement for a committed scan: name, number, finish exactly as
// saved; a novelty remark ONLY from transaction-time ownership facts.
export function announcementText(a, t = (k, v) => `${k}:${JSON.stringify(v)}`) {
  if (!a || !a.name) return '';
  if (a.event === 'pack_saved') return 'Verified opening event (facts, not a script): ' + JSON.stringify({event:'pack_saved',participant:a.participant,card:a.name,number:a.number,set:a.set_name,finish:a.printing,rarity:a.rarity||null,types:a.types||[],stage:a.subtypes||[],opening_id:a.opening_id,pack_id:a.pack_id,pack_copy_count:a.pack_copy_count,ownership:a.ownership||null,artwork:a.artwork||null}) + '. React warmly and briefly to one interesting supplied fact. Vary your phrasing; let the child keep opening or ask their own question. For this save notification, give an observation rather than asking a question; save questions for the welcome or natural conversation. No need to recite all fields. Do not invent prices or rarity odds. If artwork is null, do not describe the picture, pose, colors or anatomy; playful nicknames are ideas, not observed facts.';
  const finish = { 'Reverse Holofoil': t('scan.voice.finishReverse'), Holofoil: t('scan.voice.finishHolo'), Normal: t('scan.voice.finishNormal') }[a.printing] || a.printing || '';
  let text = t('scan.voice.committed', { name: a.name, number: a.number || '', finish });
  const o = a.ownership;
  if (o && o.new_identity === true) text += ' ' + t('scan.voice.newIdentity');
  else if (o && o.new_finish === true) text += ' ' + t('scan.voice.newFinish', { finish });
  return text.trim();
}

// Bounded, coalescing queue for post-commit announcements. One append in
// flight at a time; announcements arriving meanwhile are merged into the next
// append (oldest first, at most `maxBatch`, the rest summarised); corrections
// (priority) go before pending announcements; the queue pauses while the user
// is speaking (barge-in) and resumes afterwards. `send(text)` returns a
// promise resolved on ack (or rejects/times out); nothing is re-sent.
export class AnnouncementQueue {
  constructor({ send, canSend = () => true, maxBacklog = 6, maxBatch = 3, t } = {}) {
    this.send = send; this.canSend = canSend; this.maxBacklog = maxBacklog; this.maxBatch = maxBatch; this.t = t || ((k, v) => `${k}:${JSON.stringify(v)}`);
    this.pending = []; this.priority = []; this.inflight = null; this.paused = false; this.dropped = 0; this.sent = [];
  }
  push(announce, onDispatch = null) { const text = announcementText(announce, this.t); if (!text) return; this.pending.push({ text, at: Date.now(), announce, onDispatch }); while (this.pending.length > this.maxBacklog) { this.pending.shift(); this.dropped++; } this.flush(); }
  pushPriority(text) { if (!text) return; this.priority.push({ text, at: Date.now() }); while (this.priority.length > this.maxBacklog) this.priority.shift(); this.flush(); }
  cancelWhere(predicate) { const before=this.pending.length; this.pending=this.pending.filter(x=>!predicate(x.announce)); this.cancelled=(this.cancelled||0)+before-this.pending.length; return before-this.pending.length; }
  pause() { this.paused = true; }
  resume() { this.paused = false; this.flush(); }
  size() { return this.pending.length + this.priority.length; }
  flush() {
    if (this.inflight || this.paused || !this.canSend()) return;
    let text = null;
    if (this.priority.length) { text = this.priority.shift().text; }
    else if (this.pending.length) {
      const packEvent=this.pending[0]?.announce?.event==='pack_saved';
      const batch = this.pending.splice(0, packEvent?1:this.maxBatch);
      const rest = packEvent?0:this.pending.length + this.dropped; this.dropped = 0;
      text = batch.map(b => b.text).join(' ');
      const now = Date.now(); for (const b of batch) { try { b.onDispatch?.(now); } catch { /* evidence only */ } }
      if (rest > 0) { text += ' ' + this.t('scan.voice.andMore', { count: rest }); this.pending = []; }
    }
    if (!text) return;
    const rec = { text, at: Date.now() }; this.sent.push(rec); if (this.sent.length > 20) this.sent.splice(0, this.sent.length - 20); this.inflight = rec;
    let p; try { p = Promise.resolve(this.send(text)); } catch (e) { p = Promise.reject(e); }
    p.catch(() => {}).finally(() => { if (this.inflight === rec) this.inflight = null; this.flush(); });
  }
}


// Objective 1: a spoken answer to a pending FINISH clarification chooses ONLY
// the card; the finish itself was fixed when the question was asked. The
// backend may name the choice by token, card id, name or ordinal.
export function resolveFinishAnswer(x, pending) {
  if (!pending || pending.kind !== 'finish' || !Array.isArray(pending.candidates) || !pending.candidates.length) return null;
  const cands = pending.candidates;
  if (x?.token) { const c = cands.find(c => c.token === x.token); if (c) return c; }
  const cardId = x?.chosen_card_id || x?.card_id;
  if (cardId) { const hits = cands.filter(c => c.card_id === cardId); if (hits.length === 1) return hits[0]; }
  if (Number.isInteger(x?.ordinal) && cands[x.ordinal]) return cands[x.ordinal];
  const named = String(x?.named || '').toLowerCase().trim();
  if (named) { const hits = cands.filter(c => named.split(' ').filter(w => w.length > 1).every(w => `${c.name || ''} ${c.number || ''}`.toLowerCase().includes(w))); if (hits.length === 1) return hits[0]; }
  return null;
}

// Objective 2 evidence: when a scan was committed (authorized auto-add), when
// its save request left, when the server answered and when it was announced.
export class SaveTimeline {
  constructor() { this.rows = new Map(); }
  // Phases: committed (auto-add authorized), posted (save request left),
  // responded (server answered), popup_closed (saving panel dismissed),
  // announce_enqueued (announcement queued, NOT native audio),
  // commentary_dispatched (session.commentary.append sent to Live).
  mark(token, phase, at = Date.now(), extra = {}) { if (!token) return null; const r = this.rows.get(token) || { token }; if (r[phase] == null) r[phase] = at; Object.assign(r, extra); this.rows.set(token, r); return r; }
  get(token) { return this.rows.get(token) || null; }
  list() { return [...this.rows.values()].map(r => ({ ...r, commit_to_post_ms: r.posted != null && r.committed != null ? r.posted - r.committed : null, post_to_response_ms: r.responded != null && r.posted != null ? r.responded - r.posted : null, response_to_announce_enqueued_ms: r.announce_enqueued != null && r.responded != null ? r.announce_enqueued - r.responded : null, response_to_commentary_dispatch_ms: r.commentary_dispatched != null && r.responded != null ? r.commentary_dispatched - r.responded : null, response_to_popup_closed_ms: r.popup_closed != null && r.responded != null ? r.popup_closed - r.responded : null })); }
}
